// Pane → Interactions — what the three screens share: the window in the URL (IST days), the words for tenants and close causes, the
// formatters (IST pinned), and the charts. Server: routes/paneInteractions.js (its header carries every definition used here).
//
// Charts follow the dataviz method: single-series columns need no legend (the card title names them); the close-cause palette is the
// validated categorical order (validate_palette.js, light: all checks PASS; three hues under 3:1 on the surface, so every segment's
// count and share are written in the legend beside it — text never wears the series colour); 2px surface gaps between segments; a
// readout line answers the hovered column; 4px rounded data ends, square at the baseline; hairline solid gridlines.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import DateRange from '../insights/DateRange';
import s from './Interactions.module.css';

export const TZ = 'Asia/Kolkata';
const DAY_MS = 86400000;
export const istToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
export const addDaysIso = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

// ── the window, in the URL (a shared link opens the same read) ─────────────────────────────────────────────────────────────────
export function useWindow({ fixedTenant } = {}) {
  const [params, setParams] = useSearchParams();
  const today = istToday();
  const to = params.get('to') || today;
  const from = params.get('from') || addDaysIso(to, -6);
  const bench = params.get('bench') === '1';
  const tenant = fixedTenant || params.get('tenant') || '';
  const set = (patch) => setParams((p) => {
    const n = new URLSearchParams(p);
    Object.entries(patch).forEach(([k, v]) => { if (v === '' || v == null || v === false) n.delete(k); else n.set(k, v === true ? '1' : String(v)); });
    return n;
  });
  const apiParams = { from, to, ...(bench ? { bench: 1 } : {}), ...(tenant ? { tenant } : {}) };
  /** The window as a query string for a door to another Interactions screen (the tenant rides along unless `tenant: ''`). */
  const linkQs = (extra = {}) => {
    const o = { from, to, ...(bench ? { bench: '1' } : {}), ...(!fixedTenant && tenant ? { tenant } : {}), ...extra };
    Object.keys(o).forEach((k) => (o[k] === '' || o[k] == null) && delete o[k]);
    return `?${new URLSearchParams(o).toString()}`;
  };
  return { from, to, bench, tenant, today, set, apiParams, linkQs };
}
const PRESETS = [['today', 'Today', 0], ['7d', '7 d', 6], ['30d', '30 d', 29]];

// ── words ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const TENANT_LABEL = {
  'pane:nearby': 'Nearby', 'pane:connect': 'Connect', 'pane:weather': 'Weather', 'pane:help': 'Help', 'pane:privacy': 'Privacy',
  'pane:comfort': 'Brightness & volume', games: 'Games', 'thyrocare-diagnostics': 'Thyrocare', youtube: 'YouTube', news: 'News',
  discover: 'Discover', 'sample-sdk': 'Sample SDK', 'bench-stub': 'Bench stub',
};
const BENCH_DEMO = new Set(['youtube', 'news', 'discover', 'sample-sdk', 'bench-stub']);
export const tenantLabel = (id) => TENANT_LABEL[id] || id || '—';
export const isBuiltIn = (id) => String(id || '').startsWith('pane:');
export const tenantKind = (id, kind) => (isBuiltIn(id) ? 'Built-in pane' : kind === 'native' ? 'Native app' : kind === 'web' ? 'Web page' : 'Tenant');
export const isBenchDemo = (id) => BENCH_DEMO.has(id);
export const thingShort = (t) => String(t || '').replace('ggos-xx-', '');

// pane_closed.cause — the player's ResetCause wire values (PaneHost.kt:41-42), and the passenger's Clear ('user').
export const CAUSE_WORDS = {
  exit: 'Closed by the passenger', back: 'Back at the first screen', user: 'Cleared by the passenger', nav: 'Switched to another pane',
  idle: 'Idle — nobody touched it', ride_end: 'Ride ended', power_idle: 'Cab went to sleep', swapped: 'Replaced by an update',
  render_gone: 'Renderer crashed', activity_stop: 'Player stopped', lost_top_resumed: 'Lost the screen', resource_budget: 'Over its budget',
  '': 'No close recorded',
};
export const causeWords = (c) => CAUSE_WORDS[c || ''] || c;
export const GROUPS = [
  { key: 'passenger', label: 'The passenger closed it', help: 'Closed (×), back at the first screen, cleared, or switched to another pane', color: '#2a78d6' },
  { key: 'idle', label: 'Idle — nobody touched it', help: 'The player\'s idle backstop closed it: no touch on the pane for its window (3 min on the players the cabs ran through 31 Oct 2026; release round 15 makes it 4 min 30 s plus a 20 s “Still there?” prompt)', color: '#eb6834' },
  { key: 'ride_end', label: 'The ride ended', help: 'The occupancy ended (the passenger left) while the pane was open', color: '#1baf7a' },
  { key: 'system', label: 'Sleep or an update', help: 'The cab went to sleep (power-idle), or the tenant was replaced by an update', color: '#eda100' },
  { key: 'fault', label: 'A fault', help: 'The renderer crashed, the player stopped or lost the screen, or the tenant went over its budget', color: '#e87ba4' },
  { key: 'none', label: 'No close recorded yet', help: 'The pane has no pane_closed row — still open, or its close has not reached the lake', color: '#c9cdd8' },
];
const DEFAULT_GROUP = { exit: 'passenger', back: 'passenger', user: 'passenger', nav: 'passenger', idle: 'idle', ride_end: 'ride_end', power_idle: 'system', swapped: 'system',
  render_gone: 'fault', activity_stop: 'fault', lost_top_resumed: 'fault', resource_budget: 'fault' };
export const groupOf = (cause, map = DEFAULT_GROUP) => (!cause ? 'none' : map[cause] || 'fault');
export const groupColor = (key) => (GROUPS.find((g) => g.key === key) || GROUPS[5]).color;
/** { cause: n } → [{ key, label, color, n, causes: [[cause, n]] }] in GROUPS order, zero groups dropped. */
export function groupCauses(causes, map) {
  const by = {}; Object.entries(causes || {}).forEach(([c, n]) => { const g = groupOf(c, map); (by[g] = by[g] || []).push([c, n]); });
  return GROUPS.filter((g) => by[g.key]).map((g) => ({ ...g, n: by[g.key].reduce((a, [, n]) => a + n, 0), causes: by[g.key].sort((a, b) => b[1] - a[1]) }));
}

// ── formatters (IST pinned) ────────────────────────────────────────────────────────────────────────────────────────────────────
export const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
export const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : '—');
/** A duration in words, to the second under an hour: 42 s · 3 min 05 s · 1 h 04 min. */
export function dur(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return '—';
  const t = Math.round(Number(ms) / 1000);
  if (t < 60) return `${t} s`;
  if (t < 3600) return `${Math.floor(t / 60)} min ${String(t % 60).padStart(2, '0')} s`;
  return `${Math.floor(t / 3600)} h ${String(Math.round((t % 3600) / 60)).padStart(2, '0')} min`;
}
/** The same duration, compact, for a stat tile's big number: 42s · 29m 39s · 2h 20m. */
export function durShort(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return '—';
  const t = Math.round(Number(ms) / 1000);
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${String(t % 60).padStart(2, '0')}s`;
  return `${Math.floor(t / 3600)}h ${String(Math.round((t % 3600) / 60)).padStart(2, '0')}m`;
}
const fmtParts = (ms, opts) => new Date(Number(ms)).toLocaleString('en-IN', { timeZone: TZ, ...opts });
export const clock = (ms) => (ms == null ? '—' : fmtParts(ms, { hour: '2-digit', minute: '2-digit', hour12: false }));
export const clockS = (ms) => (ms == null ? '—' : fmtParts(ms, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
export const dayTime = (ms) => (ms == null ? '—' : fmtParts(ms, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }));
export const dayWords = (iso) => new Date(`${iso}T12:00:00+05:30`).toLocaleDateString('en-IN', { timeZone: TZ, weekday: 'short', day: '2-digit', month: 'short' });
export const dayShort = (iso) => new Date(`${iso}T12:00:00+05:30`).toLocaleDateString('en-IN', { timeZone: TZ, day: 'numeric', month: 'short' });
export const windowWords = (w) => (!w ? '' : w.from === w.to ? `${dayWords(w.from)} · IST` : `${dayWords(w.from)} – ${dayWords(w.to)} · ${w.days} IST days`);
/** Consecutive repeats folded: home → home → tictactoe reads home → tictactoe. */
export const screensWords = (xs) => (xs || []).filter((x, i, a) => i === 0 || a[i - 1] !== x).join(' → ');

export function Help({ text }) {
  return <span title={text} style={{ color: 'var(--ink-faint)', display: 'inline-flex', cursor: 'help' }}><Info size={12} aria-label={text} /></span>;
}

// ── the filter bar ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
export function FilterBar({ win, data, showBench = true, showTenants = true, ob }) {
  const active = PRESETS.find(([, , back]) => win.to === win.today && win.from === addDaysIso(win.today, -back));
  return (
    <>
      <div className={s.bar}>
        <div className={ob.segCtl} role="group" aria-label="Range">
          {PRESETS.map(([k, label, back]) => (
            <button key={k} type="button" className={`${ob.segBtn} ${active && active[0] === k ? ob.segOn : ''}`}
              onClick={() => win.set({ from: addDaysIso(win.today, -back), to: win.today })}>{label}</button>))}
        </div>
        <DateRange from={win.from} to={win.to} onChange={(f, t) => win.set({ from: f, to: t })} />
        {showBench && (
          <label className={`${s.benchToggle} ${win.bench ? s.benchOn : ''}`} title={`The bench is ${(data?.bench_things || ['ggos-xx-237kvf']).join(', ')} (the founder's bench screen, not a cab) and every test device. Excluded unless ticked.`}>
            <input type="checkbox" checked={win.bench} onChange={(e) => win.set({ bench: e.target.checked })} /> Include bench
          </label>)}
        <span className={ob.spacer} />
        <span className={s.windowWords} data-window>{windowWords(data?.window)}{win.bench ? ' · bench included' : ''}</span>
      </div>
      {showTenants && data?.tenant_options?.length > 0 && (
        <div className={s.tenantRow} role="group" aria-label="Tenant">
          <span className={s.tenantRowLabel}>Tenant</span>
          <button type="button" className={`${ob.chip} ${!win.tenant ? ob.chipOn : ''}`} onClick={() => win.set({ tenant: '' })}>all</button>
          {data.tenant_options.map((t) => (
            <button key={t.tenant_id} type="button" className={`${ob.chip} ${win.tenant === t.tenant_id ? ob.chipOn : ''}`}
              onClick={() => win.set({ tenant: win.tenant === t.tenant_id ? '' : t.tenant_id })}>{tenantLabel(t.tenant_id)} · {t.panes}</button>))}
        </div>)}
    </>
  );
}

// ── charts ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
function useWidth() {
  const ref = useRef(null); const [w, setW] = useState(600);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(ref.current); return () => ro.disconnect();
  }, []);
  return [ref, w];
}
const niceMax = (v) => { if (v <= 4) return 4; const p = 10 ** Math.floor(Math.log10(v)); const m = v / p; return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p; };
/** A rounded-top column segment: 4px data end, square at the base. */
const colPath = (x, y, w, h, round) => {
  if (h <= 0) return '';
  const r = round ? Math.min(4, w / 2, h) : 0;
  return `M${x},${y + h}V${y + r}${r ? `Q${x},${y} ${x + r},${y}` : ''}H${x + w - r}${r ? `Q${x + w},${y} ${x + w},${y + r}` : ''}V${y + h}Z`;
};
/** Columns over a category axis. rows: [{ key, label, values: [..] }], series: [{ name, color }] (stacked bottom-up), readout(row) → node. */
export function Columns({ rows, series, height = 150, readout, ariaLabel }) {
  const [ref, w] = useWidth(); const [hover, setHover] = useState(null);
  const padL = 30; const padB = 20; const padT = 6; const H = height; const plotW = w - padL; const plotH = H - padB - padT;
  const max = niceMax(Math.max(1, ...rows.map((r) => r.values.reduce((a, b) => a + (b || 0), 0))));
  const band = plotW / Math.max(1, rows.length); const bw = Math.max(3, Math.min(24, band * 0.68));
  const every = Math.max(1, Math.ceil((rows.length * 34) / plotW));
  const y = (v) => padT + plotH - (v / max) * plotH;
  const ticks = Number.isInteger(max / 2) ? [0, max / 2, max] : [0, max];
  const hovered = hover != null ? rows[hover] : null;
  return (
    <div ref={ref}>
      <div className={s.readout} aria-live="polite">{hovered ? readout(hovered) : <span style={{ color: 'var(--ink-faint)' }}>Point at a column to read it.</span>}</div>
      <svg className={s.chart} width={w} height={H} role="img" aria-label={ariaLabel} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (<g key={t}><line x1={padL} x2={w} y1={y(t)} y2={y(t)} stroke="var(--line-soft)" strokeWidth="1" />
          <text x={padL - 6} y={y(t) + 3.5} textAnchor="end">{num(t)}</text></g>))}
        {rows.map((r, i) => {
          const cx = padL + band * i + band / 2; let acc = 0; const last = r.values.reduce((k, v, j) => (v > 0 ? j : k), -1);
          return (
            <g key={r.key}>
              {hover === i && <rect x={padL + band * i} y={padT} width={band} height={plotH} fill="var(--iris-soft)" opacity=".6" />}
              {r.values.map((v, j) => {
                if (!v) return null; const h = (v / max) * plotH; const top = y(acc + v); const gap = acc > 0 ? 2 : 0; acc += v;
                return <path key={j} d={colPath(cx - bw / 2, top, bw, Math.max(0, h - gap), j === last)} fill={series[j].color} />;
              })}
              {i % every === 0 && <text x={cx} y={H - 5} textAnchor="middle">{r.label}</text>}
              <rect x={padL + band * i} y={padT} width={band} height={plotH + padB} fill="transparent" onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={-1} />
            </g>);
        })}
        <line x1={padL} x2={w} y1={y(0)} y2={y(0)} stroke="var(--line)" strokeWidth="1" />
      </svg>
      {series.length > 1 && <div className={s.chartLegend}>{series.map((x) => <span key={x.name}><i className={s.swatch} style={{ background: x.color }} />{x.name}</span>)}</div>}
    </div>
  );
}

/** The ride funnel: nested by construction (server: each step is the conjunction of the ones before). */
export function Funnel({ f }) {
  if (!f) return null;
  const steps = [
    ['Rides', f.rides, 'Occupancies that STARTED in the window (occupancy_started, the device clock): touch- or presence-started.'],
    ['Touched the screen', f.rides_touched, 'Rides with at least one touch (occupancy_ended.touches > 0) or a pane opened. A ride still in progress counts only once it opens a pane.'],
    ['Opened a pane', f.rides_with_pane, 'Rides in which at least one pane opened (pane_opened in the window, naming this ride).'],
    ['A tenant app mounted', f.rides_with_mount, 'Of those, rides where a native tenant app was mounted in a pane (tenant_mounted). Built-in panes (Nearby, Connect, Weather…) never mount.'],
    ['Used inside the app', f.rides_with_use, 'Of those, rides where the mounted app reported at least one in-tenant event (tenant_event, or a web page\'s own steps).'],
  ];
  const max = Math.max(1, f.rides);
  return (
    <div>
      {steps.map(([label, n, help]) => (
        <div key={label} className={s.fRow} data-funnel={label}>
          <span className={s.fLabel}>{label} <Help text={help} /></span>
          <span className={s.fTrack}><span className={s.fFill} style={{ width: `${(100 * n) / max}%` }} /></span>
          <span className={s.fNum}>{num(n)}<small>{label === 'Rides' ? '' : pct(n, f.rides)}</small></span>
        </div>))}
      <div className={s.fSep} />
      <div className={s.fRow}>
        <span className={s.fLabel}>Scanned a QR <Help text="Rides that opened a pane and have at least one QR scan (scan_events, by the ride's occupancy id) — any pane, built-in or tenant." /></span>
        <span className={s.fTrack}><span className={`${s.fFill} ${s.fFillSide}`} style={{ width: `${(100 * f.rides_with_scan) / max}%` }} /></span>
        <span className={s.fNum}>{num(f.rides_with_scan)}<small title="of the rides that opened a pane">{pct(f.rides_with_scan, f.rides_with_pane)}</small></span>
      </div>
    </div>
  );
}

/** How panes close: one 100% stacked bar and a legend carrying each group's count, share and causes. The idle row is highlighted. */
export function CloseCauses({ causes, map }) {
  const groups = useMemo(() => groupCauses(causes, map), [causes, map]);
  const total = groups.reduce((a, g) => a + g.n, 0);
  if (!total) return <p className={s.cardSub}>No pane opened in this window.</p>;
  return (
    <div>
      <div className={s.stack} role="img" aria-label={groups.map((g) => `${g.label} ${g.n}`).join(', ')}>
        {groups.map((g) => <span key={g.key} className={s.stackSeg} style={{ flex: g.n, background: g.color }} title={`${g.label}: ${g.n} (${pct(g.n, total)})`} />)}
      </div>
      <div className={s.legend}>
        {groups.map((g) => (
          <div key={g.key} className={`${s.lgRow} ${g.key === 'idle' ? s.lgHot : ''}`} data-close-group={g.key} title={g.help}>
            <i className={s.swatch} style={{ background: g.color }} />
            <span>{g.label}{g.causes.length > 1 || g.key === 'fault' || g.key === 'system' ? <small> — {g.causes.map(([c, n]) => `${causeWords(c).toLowerCase()} ${n}`).join(' · ')}</small> : null}</span>
            <span><b>{num(g.n)}</b> <small>{pct(g.n, total)}</small></span>
          </div>))}
      </div>
    </div>
  );
}

/** A row's close mix as a thin 100% bar (the by-tenant and cab tables). */
export function CauseMini({ causes, map }) {
  const groups = groupCauses(causes, map); const total = groups.reduce((a, g) => a + g.n, 0);
  return (
    <span className={s.miniStack} title={groups.map((g) => `${g.label} ${g.n}`).join(' · ')}>
      {groups.map((g) => <span key={g.key} style={{ flex: g.n, background: g.color }} />)}
      {!total && <span style={{ flex: 1 }} />}
    </span>);
}
export { s as ix };
